import { ChevronIcon } from '@/assets';
import { TitleSection } from '@/components';
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/react';
import { Content } from '@prismicio/client';
import { PrismicNextLink } from '@prismicio/next';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';

/**
 * Props for `Faqs`.
 */
export type FaqsProps = SliceComponentProps<Content.FaqsSlice>;

const getStyles = (colors: string) => {
  switch (colors) {
    case 'light':
      return {
        background: 'bg-mint-green-gradient-5',
        textColor: 'text-black',
        chevronFill: '#AAA',
        separatorBg: 'bg-[#AAA]',
        containerHorizontalPadding: 'py-20',
      };
    case 'dark':
    default:
      return {
        background: '',
        textColor: 'text-white',
        chevronFill: 'white',
        separatorBg: 'bg-white',
        containerHorizontalPadding: 'py-0',
      };
  }
};

/**
 * Component for "Faqs" Slices.
 */
const Faqs = ({ slice }: FaqsProps): JSX.Element => {
  const {
    primary: {
      title,
      subtitle,
      extra_question_title,
      extra_question_subtitle,
      extra_question_button_label,
      extra_question_button_link,
      faqs_group,
      colors,
    },
  } = slice;

  const styles = getStyles(colors);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={styles.background}
    >
      <div
        className={`px-5 md:px-16 ${styles.containerHorizontalPadding} max-w-screen-2xl mx-auto ${styles.textColor}`}
      >
        <TitleSection title={title} description={subtitle} />

        <div className="px-10 max-w-[768px] mx-auto -mt-4">
          {faqs_group.map((faq, index) => (
            <div className="mb-6" key={index}>
              <div className={`w-full h-[1px] ${styles.separatorBg}`} />
              <Disclosure>
                <DisclosureButton className="group flex gap-2 pt-5 justify-between w-full">
                  <PrismicRichText
                    field={faq.question}
                    components={{
                      paragraph: ({ children }) => (
                        <p className="text-medium-bold">{children}</p>
                      ),
                    }}
                  />
                  <div className="group-data-[open]:-rotate-180 transition-transform duration-200">
                    <ChevronIcon
                      fill={styles.chevronFill}
                      width={24}
                      height={24}
                    />
                  </div>
                </DisclosureButton>
                <DisclosurePanel
                  transition
                  className="mt-4 origin-top transition duration-200 ease-out data-[closed]:-translate-y-6 data-[closed]:opacity-0"
                >
                  <PrismicRichText
                    field={faq.description}
                    components={{
                      paragraph: ({ children }) => (
                        <p className="text-regular-normal">{children}</p>
                      ),
                    }}
                  />
                </DisclosurePanel>
              </Disclosure>
            </div>
          ))}
          <div className={`w-full h-[1px] ${styles.separatorBg}`} />
        </div>

        {extra_question_button_label && (
          <div className="flex flex-col items-center mt-10 gap-y-12">
            <TitleSection
              title={extra_question_title}
              description={extra_question_subtitle}
            />
            <PrismicNextLink
              field={extra_question_button_link}
              className="button-bordered -mt-28 mb-10"
            >
              <span>{extra_question_button_label}</span>
            </PrismicNextLink>
          </div>
        )}
      </div>
    </section>
  );
};

export default Faqs;
